/**
 * Replay viewer UI: fixed bottom control bar (speed, seek ±5/±10, timeline).
 * Does not float over the canvas — reserves its own layout space via body class.
 */

import type { ReplayPlaybackSpeed } from "./playback";
import {
  getReplayDurationMs,
  getReplayPlaybackSpeed,
  getReplayProgress01,
  getReplaySimMs,
  isReplayPlaybackActive,
  requestReplaySeek,
  setReplayPlaybackSpeed,
} from "./playback";

const SPEEDS: ReplayPlaybackSpeed[] = [0.5, 1, 2, 4];

export type ReplayUiHooks = {
  onReturn: () => void;
  onSeekRestart: () => void;
};

let hooks: ReplayUiHooks | null = null;
let scrubbing = false;

export function configureReplayUi(next: ReplayUiHooks): void {
  hooks = next;
}

function el<T extends HTMLElement>(id: string): T | null {
  return document.getElementById(id) as T | null;
}

export function showReplayControls(): void {
  const bar = el("replayControls");
  if (bar) bar.classList.remove("hidden");
  document.body.classList.add("replay-playback");
  syncReplayControlsUi();
}

export function hideReplayControls(): void {
  const bar = el("replayControls");
  if (bar) bar.classList.add("hidden");
  document.body.classList.remove("replay-playback");
  scrubbing = false;
}

function formatReplayTime(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, "0")}`;
}

export function syncReplayControlsUi(): void {
  if (!isReplayPlaybackActive()) return;
  const dur = getReplayDurationMs();
  const cur = getReplaySimMs();
  const timeEl = el<HTMLElement>("replayTimeLabel");
  if (timeEl) timeEl.textContent = `${formatReplayTime(cur)} / ${formatReplayTime(dur)}`;

  const track = el<HTMLInputElement>("replaySeekBar");
  if (track && !scrubbing) {
    track.max = String(Math.max(1, dur));
    track.value = String(Math.min(dur, cur));
  }

  const speed = getReplayPlaybackSpeed();
  for (const sp of SPEEDS) {
    const btn = el<HTMLButtonElement>(`replaySpeed${String(sp).replace(".", "_")}`);
    if (!btn) continue;
    btn.classList.toggle("replaySpeedBtn--active", sp === speed);
  }
}

function seekBy(deltaMs: number): void {
  if (!isReplayPlaybackActive()) return;
  const target = getReplaySimMs() + deltaMs;
  requestReplaySeek(target);
  hooks?.onSeekRestart();
  syncReplayControlsUi();
}

function seekTo(ms: number): void {
  if (!isReplayPlaybackActive()) return;
  requestReplaySeek(ms);
  hooks?.onSeekRestart();
  syncReplayControlsUi();
}

export function wireReplayControls(): void {
  const ret = el<HTMLButtonElement>("replayReturnBtn");
  if (ret) {
    ret.addEventListener("click", () => hooks?.onReturn());
  }

  for (const sp of SPEEDS) {
    const id = `replaySpeed${String(sp).replace(".", "_")}`;
    const btn = el<HTMLButtonElement>(id);
    if (!btn) continue;
    btn.addEventListener("click", () => {
      setReplayPlaybackSpeed(sp);
      syncReplayControlsUi();
    });
  }

  const bindSeek = (id: string, delta: number) => {
    const b = el<HTMLButtonElement>(id);
    if (b) b.addEventListener("click", () => seekBy(delta));
  };
  bindSeek("replayBack10Btn", -10000);
  bindSeek("replayBack5Btn", -5000);
  bindSeek("replayFwd5Btn", 5000);
  bindSeek("replayFwd10Btn", 10000);

  const track = el<HTMLInputElement>("replaySeekBar");
  if (track) {
    track.addEventListener("pointerdown", () => {
      scrubbing = true;
    });
    const endScrub = () => {
      if (!scrubbing) return;
      scrubbing = false;
      seekTo(Number(track.value) || 0);
    };
    track.addEventListener("pointerup", endScrub);
    track.addEventListener("change", endScrub);
    track.addEventListener("input", () => {
      if (!scrubbing) return;
      const timeEl = el<HTMLElement>("replayTimeLabel");
      const dur = getReplayDurationMs();
      if (timeEl) {
        timeEl.textContent = `${formatReplayTime(Number(track.value) || 0)} / ${formatReplayTime(dur)}`;
      }
    });
  }
}

/** Create a underlined "View Replay" control (button styled as text link). */
export function createViewReplayLink(onClick: () => void): HTMLButtonElement {
  const btn = document.createElement("button");
  btn.type = "button";
  btn.className = "viewReplayLink";
  btn.textContent = "View Replay";
  btn.addEventListener("click", (e) => {
    e.preventDefault();
    e.stopPropagation();
    onClick();
  });
  return btn;
}

export function replayProgressForHud(): number {
  return getReplayProgress01();
}
